import { useState, useCallback } from 'react';
import { GridRowSelectionModel } from '@mui/x-data-grid/models/gridRowSelectionModel';

interface UseDataGridSelectionProps<T> {
  data: T[];
  getItemId: (item: T) => string;
}

export const useDataGridSelection = <T,>({ data, getItemId }: UseDataGridSelectionProps<T>) => {
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const handleRowSelectionChange = useCallback((selectionModel: GridRowSelectionModel) => {
    if (selectionModel.length > 0) {
      const selectedId = selectionModel[0] as string;
      const selectedItemData = data.find(item => getItemId(item) === selectedId);
      setSelectedItem(selectedItemData || null);
      setSelectedRowId(selectedId);
    } else {
      setSelectedItem(null);
      setSelectedRowId(null);
    }
  }, [data, getItemId]);

  const handleCloseDetailPanel = useCallback(() => {
    setSelectedItem(null);
    setSelectedRowId(null);
  }, []);

  return {
    selectedItem,
    selectedRowId,
    handleRowSelectionChange,
    handleCloseDetailPanel,
    setSelectedItem,
    setSelectedRowId
  };
};

interface UsePaginationProps {
  initialPage?: number;
  initialPageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
}

export const usePagination = ({ 
  initialPage = 0, 
  initialPageSize = 10,
  onPageChange
}: UsePaginationProps = {}) => {
  const [pagination, setPagination] = useState({
    page: initialPage,
    pageSize: initialPageSize,
    total: 0,
  });

  const handlePageChange = useCallback((newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    onPageChange?.(newPage, pagination.pageSize);
  }, [pagination.pageSize, onPageChange]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, page: 0 }));
    onPageChange?.(0, newPageSize);
  }, [onPageChange]);

  const handlePaginationModelChange = useCallback((model: { page: number; pageSize: number }) => {
    if (model.page !== pagination.page) {
      handlePageChange(model.page);
    }
    if (model.pageSize !== pagination.pageSize) {
      handlePageSizeChange(model.pageSize);
    }
  }, [pagination.page, pagination.pageSize, handlePageChange, handlePageSizeChange]);

  const setTotal = useCallback((total: number) => {
    setPagination(prev => ({ ...prev, total }));
  }, []);

  return {
    pagination,
    setPagination,
    handlePageChange,
    handlePageSizeChange,
    handlePaginationModelChange,
    setTotal
  };
};
